<script setup lang="ts">
import CommonInput from "@/components/common/CommonInput.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import { useNotification } from "@/utils/notification";
import { ref } from "vue";
import { authService } from "@/features/auth/auth.service";
import { Eye, EyeOff } from "utruckin-icons";
import { useRouter } from "vue-router";
import { setToken } from "@/utils/token";
import logo from "@/assets/images/logo-auth.svg";

const { pushNotification } = useNotification();
const router = useRouter();

const email = ref<string>("");
const password = ref<string>("");
const loading = ref<boolean>(false);
const errorMessage = ref<string>("");
const typeInput = ref<"password" | "text">("password");

const isValidEmail = (val: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(val);
};

const login = async () => {
  errorMessage.value = "";

  if (!email.value.trim()) {
    errorMessage.value = "Email is required";
    pushNotification(errorMessage.value, "error");
    return;
  }

  if (!isValidEmail(email.value)) {
    errorMessage.value = "Invalid email format";
    pushNotification(errorMessage.value, "error");
    return;
  }

  if (password.value.length < 6) {
    errorMessage.value = "Password must be at least 6 characters long";
    pushNotification(errorMessage.value, "error");
    return;
  }

  loading.value = true;
  try {
    const { data } = await authService.login({
      email: email.value,
      password: password.value,
    });
    if (data.access_token) {
      const profile = data.profile;

      if (!profile?.role) {
        pushNotification("Unable to load user profile", "error");
        return;
      }

      setToken(data.access_token);

      if (profile.role === "super_admin") {
        localStorage.removeItem("organizationId");
        await router.replace("/organization");
      } else {
        if (!profile.organization_id) {
          pushNotification(
            "Organization is not assigned to this user",
            "error"
          );
          return;
        }

        localStorage.setItem("organizationId", profile.organization_id);
        await router.replace("/all-tasks");
      }

      pushNotification("Login successful", "success");
    }
  } catch (err: any) {
    const message = err?.response?.data?.detail;
    pushNotification(message, "error");
    return;
  } finally {
    loading.value = false;
  }
};

const handleAppendClick = () => {
  if (typeInput.value === "password") {
    typeInput.value = "text";
  } else {
    typeInput.value = "password";
  }
};
</script>

<template>
  <div class="v-login">
    <div class="main">
      <div class="logo">
        <img :src="logo" alt="company_logo" />
      </div>
      <div class="body">
        <div class="body__header">Sign in</div>

        <div class="body__body">
          <CommonInput
            v-model:model-value="email"
            label="Email"
            autocomplete="email"
            placeholder="Email"
          />

          <CommonInput
            :type="typeInput"
            v-model:model-value="password"
            label="Password"
            autocomplete="password"
            placeholder="Password"
            :append-icon="typeInput == 'text' ? EyeOff : Eye"
            @append-icon-click="handleAppendClick"
          />
        </div>
        <div class="body__footer">
          <CommonButton @click="login" type="primary" label="Sign In" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.v-login {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--color-system-background);

  .main {
    width: 480px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    background: transparent;

    .logo {
      background: transparent;

      img {
        background: transparent;
      }
    }

    .body {
      width: 100%;
      background: var(--color-background);
      border-radius: 24px;
      border: 1px solid var(--color-border);
      box-shadow: 0 0 12px 0 #9d9b9b1a;
      padding: 24px;
      margin-top: 56px;

      &__header {
        display: flex;
        margin-bottom: 16px;
        font-weight: 700;
        font-size: 24px;
        line-height: 32px;
        color: var(--color-label);
      }

      &__body {
        border-top: 1px solid var(--color-border);
        border-bottom: 1px solid var(--color-border);
        padding: 24px 0;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      &__footer {
        display: flex;
        justify-content: end;
        padding-top: 24px;
      }
    }
  }
}
</style>
